/**
 * Agent assist (V1-E11-T04): the live calls whose human agent is helped, and one of them followed as it is spoken:
 * its transcript (the customer and the agent), the suggestions after the customer speaks (a passage from the
 * knowledge base, a next step), and what the agent must still say (nudges, due and done). Everything comes from
 * the event stream; the assist profiles that say how the agents are helped are below.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, CircleAlert, Radio } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { data } from "../api/client";
import type { StreamedEvent, StreamState } from "../api/events";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useEventStream } from "../calls/live";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState, Loading } from "../ui/states";
import { Table } from "../ui/Table";
import { Profiles } from "./Profiles";

export interface Said {
  seq: number;
  speaker: "customer" | "agent";
  text: string;
}

export interface Suggestion {
  id: string;
  kind: "knowledge" | "next_action";
  text: string;
  turn: number;
  title?: string | null;
  uri?: string | null;
  score?: number | null;
  why?: string | null;
}

export interface NudgeState {
  name: string;
  text: string;
  state: "due" | "done";
  turn: number;
}

/** What the call's events say so far: its lines, its suggestions (newest first) and each nudge's latest state. */
export function assisted(events: StreamedEvent[]) {
  const lines: Said[] = [];
  const suggestions: Suggestion[] = [];
  const nudges = new Map<string, NudgeState>();
  let ended = false;
  for (const e of events) {
    const d = e.data;
    if (e.type === "transcript.final") {
      const speaker = d.speaker === "agent" || d.speaker === "customer" ? d.speaker : d.role === "user" ? "customer" : "agent";
      lines.push({ seq: Number(d.seq), speaker, text: String(d.text ?? "") });
    } else if (e.type === "assist.suggestion") {
      suggestions.unshift({ id: e.id, kind: d.kind as Suggestion["kind"], text: String(d.text ?? ""), turn: Number(d.turn),
        title: d.title as string | null, uri: d.uri as string | null, score: d.score as number | null,
        why: d.why as string | null });  // prettier-ignore
    } else if (e.type === "assist.nudge") {
      const name = String(d.nudge);
      if (nudges.get(name)?.state !== "done") {
        nudges.set(name, { name, text: String(d.text ?? ""), state: d.state === "done" ? "done" : "due", turn: Number(d.turn) });
      }
    } else if (e.type === "call.completed") {
      ended = true;
    }
  }
  lines.sort((a, b) => a.seq - b.seq);
  const due = [...nudges.values()].sort((a, b) => (a.state === b.state ? a.turn - b.turn : a.state === "due" ? -1 : 1));
  return { lines, suggestions, nudges: due, ended };
}

function StreamBadge({ state }: { state: StreamState }) {
  const t = useT();
  return (
    <span role="status" className="inline-flex items-center gap-1 text-xs text-[var(--color-muted)]">
      <Radio size={12} aria-hidden="true" className={state === "live" ? "text-[var(--color-success)]" : ""} />
      {t(`live.state.${state}`)}
    </span>
  );
}

/** The followed call's events so far, page by page, then the stream from the last of them. */
function useAssistedCall(callId: string) {
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const initial = useQuery({
    queryKey: [`assist:${callId}:events`, tenant],
    queryFn: async () => {
      const all: StreamedEvent[] = [];
      let cursor: string | undefined;
      do {
        const page = await data(api.GET("/v1/calls/{call_id}/events", {
          params: { path: { call_id: callId }, query: { limit: 200, cursor } },
        }));
        all.push(...(page.data as unknown as StreamedEvent[]));
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return all;
    },
    staleTime: Infinity,
  });
  const [streamed, setStreamed] = useState<StreamedEvent[]>([]);
  const state = useEventStream({ callId, since: initial.data?.at(-1)?.id, enabled: initial.isSuccess }, (e) =>
    setStreamed((es) => (es.some((x) => x.id === e.id) ? es : [...es, e])),
  );
  const loaded = initial.data ?? [];
  const seen = new Set(loaded.map((e) => e.id));
  return { initial, events: [...loaded, ...streamed.filter((e) => !seen.has(e.id))], state };
}

function Transcript({ lines }: { lines: Said[] }) {
  const t = useT();
  const end = useRef<HTMLLIElement>(null);
  useEffect(() => end.current?.scrollIntoView?.({ block: "nearest" }), [lines.length]);
  if (!lines.length) return <p className="text-sm text-[var(--color-muted)]">{t("assist.nothingSaid")}</p>;
  return (
    <ol aria-label={t("assist.transcript")} className="max-h-[28rem] space-y-2 overflow-y-auto text-sm">
      {lines.map((line, i) => (
        <li key={line.seq} ref={i === lines.length - 1 ? end : undefined}
          className={`rounded-md px-3 py-2 ${line.speaker === "agent" ? "ml-8 bg-[var(--color-surface)]" : "mr-8 border border-[var(--color-border)]"}`}>
          <span className="block text-xs font-medium text-[var(--color-muted)]">{t(`assist.speaker.${line.speaker}`)}</span>
          {line.text}
        </li>
      ))}
    </ol>
  );
}

function Nudges({ nudges }: { nudges: NudgeState[] }) {
  const t = useT();
  if (!nudges.length) return <p className="text-sm text-[var(--color-muted)]">{t("assist.noNudges")}</p>;
  return (
    <ul aria-label={t("assist.nudges")} className="space-y-2 text-sm">
      {nudges.map((n) => (
        <li key={n.name} aria-label={`${n.name}: ${t(`assist.nudge.${n.state}`)}`}
          className={`flex gap-2 rounded-md border p-2 ${n.state === "due" ? "border-[var(--color-danger)]" : "border-[var(--color-border)] text-[var(--color-muted)]"}`}>
          {n.state === "due"
            ? <CircleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-[var(--color-danger)]" />
            : <CheckCircle2 size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-[var(--color-success)]" />}
          <div>
            <span className="block text-xs font-medium">
              {t(`assist.nudge.${n.state}`)} · <span className="font-mono">{n.name}</span>
            </span>
            {n.text}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Suggestions({ suggestions }: { suggestions: Suggestion[] }) {
  const t = useT();
  if (!suggestions.length) return <p className="text-sm text-[var(--color-muted)]">{t("assist.noSuggestions")}</p>;
  return (
    <ol aria-label={t("assist.suggestions")} className="max-h-[28rem] space-y-2 overflow-y-auto text-sm">
      {suggestions.map((s) => (
        <li key={s.id} className="rounded-md border border-[var(--color-border)] p-2">
          <span className="block text-xs font-medium text-[var(--color-muted)]">
            {s.kind === "knowledge"
              ? t("assist.fromKnowledge", { title: s.title ?? "—", score: Math.round((s.score ?? 0) * 100) })
              : t("assist.nextStep")}
          </span>
          <p>{s.text}</p>
          {s.why && <p className="text-xs text-[var(--color-muted)]">{t("assist.why", { why: s.why })}</p>}
          {s.uri && (
            <a href={s.uri} target="_blank" rel="noreferrer" className="text-xs text-[var(--color-primary)] underline">
              {t("assist.open", { title: s.title ?? s.uri })}
            </a>
          )}
        </li>
      ))}
    </ol>
  );
}

/** One assisted call, followed as it is spoken. */
export function AssistedCall({ callId, onClose }: { callId: string; onClose: () => void }) {
  const t = useT();
  const { initial, events, state } = useAssistedCall(callId);
  if (initial.isPending) return <Loading label={t("assist.following")} />;
  if (initial.isError) return <ErrorState error={initial.error} onRetry={() => void initial.refetch()} />;
  const { lines, suggestions, nudges, ended } = assisted(events);
  return (
    <section aria-labelledby="assisted-call" className="space-y-4 rounded-lg border border-[var(--color-border)] p-4">
      <header className="flex flex-wrap items-center gap-3">
        <h2 id="assisted-call" className="text-lg font-semibold">{t("assist.call")}</h2>
        <span className="font-mono text-xs text-[var(--color-muted)]">{callId}</span>
        {!ended && <StreamBadge state={state} />}
        <Button variant="ghost" className="ml-auto" onClick={onClose}>{t("assist.stop")}</Button>
      </header>
      {ended && (
        <p role="status" className="text-sm">
          {t("assist.ended")}{" "}
          <Link to="/calls/$callId" params={{ callId }} className="text-[var(--color-primary)] underline">{t("assist.record")}</Link>
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-2">
          <h3 className="font-semibold">{t("assist.transcript")}</h3>
          <Transcript lines={lines} />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold">{t("assist.nudges")}</h3>
            <Nudges nudges={nudges} />
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">{t("assist.suggestions")}</h3>
            <Suggestions suggestions={suggestions} />
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveAssisted({ names }: { names: Map<string, string> }) {
  const t = useT();
  const format = useFormat();
  const [followed, setFollowed] = useState<string | null>(null);
  const live = useApiQuery("calls:live", (api) => api.GET("/v1/calls/live"), { refetchInterval: 15_000 });
  const refresh = useRef<ReturnType<typeof setTimeout>[]>([]);
  const state = useEventStream({ types: ["call.state_changed", "call.completed"] }, () => {
    refresh.current.forEach(clearTimeout);
    refresh.current = [400, 2500].map((ms) => setTimeout(() => void live.refetch(), ms));
  });
  useEffect(() => () => refresh.current.forEach(clearTimeout), []);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">{t("assist.live")}</h2>
        <StreamBadge state={state} />
      </div>
      <Section query={live}>
        {(page) => {
          const rows = page.data.filter((c) => c.assist_profile);
          return rows.length === 0 ? (
            <Empty title={t("assist.none")}><p>{t("assist.noneBody")}</p></Empty>
          ) : (
            <Table caption={t("assist.live")} rows={rows} rowKey={(c) => c.call_id}
              columns={[
                { header: t("live.since"), cell: (c) => format.time(c.started_at) },
                { header: t("calls.col.from"), cell: (c) => <span className="font-mono">{c.from ?? "—"}</span> },
                { header: t("calls.col.to"), cell: (c) => <span className="font-mono">{c.to ?? "—"}</span> },
                { header: t("assist.profile"), cell: (c) => names.get(c.assist_profile ?? "") ?? c.assist_profile },
                { header: t("live.state"), cell: (c) => c.state ?? "—" },
                {
                  header: t("assist.follow"),
                  cell: (c) => (
                    <Button variant={followed === c.call_id ? "primary" : "secondary"} aria-pressed={followed === c.call_id}
                      aria-label={t("assist.followOne", { from: c.from ?? c.call_id })} onClick={() => setFollowed(c.call_id)}>
                      {t("assist.follow")}
                    </Button>
                  ),
                },
              ]}
            />
          );
        }}
      </Section>
      {followed && <AssistedCall key={followed} callId={followed} onClose={() => setFollowed(null)} />}
    </div>
  );
}

export function AssistPage() {
  const t = useT();
  const canReadProfiles = useCan("agents:read");
  const profiles = useApiQuery("assist-profiles", (api) => api.GET("/v1/assist-profiles"),
    { enabled: canReadProfiles });
  const names = new Map((profiles.data?.data ?? []).map((p) => [p.id, p.name]));
  return (
    <Page title={t("nav.assist")} description={t("assist.description")}>
      <LiveAssisted names={names} />
      {canReadProfiles && <Profiles query={profiles} />}
    </Page>
  );
}
